import React from 'react';
import { Button, DatePicker, Form, Input, Switch, Upload } from 'antd';
import { openNotification } from '../../../utils';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { createMeasuringInstrumentCalibration, updateMeasuringInstrumentCalibration, getMeasuringInstrumentCalibration } from '../services/measuring-instrument-calibrationService';
import { CreateMeasuringInstrumentCalibrationDto } from '../types/MeasuringInstrumentCalibration';
import type { UploadFile } from 'antd/es/upload/interface';

export default function MeasuringInstrumentCalibrationForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateMeasuringInstrumentCalibrationDto>();
  const normalizeUploadEvent = (e: any) => (Array.isArray(e?.fileList) ? e.fileList.slice(-1) : e?.fileList);
  const buildFileListFromUrl = (url: string, uid: string): UploadFile[] => {
    const name = url.split('/').pop() || 'file';
    return [{ uid, name, status: 'done', url }];
  };
  const uploadSingleDocument = async (files: UploadFile[] | undefined, code: string) => {
    if (!Array.isArray(files) || !files.length) return null;
    const [file] = files;
    if (file?.originFileObj) {
      const fd = new FormData();
      fd.append('code', code);
      fd.append('document', file.originFileObj);
      const uploaded = await apiFetch('/documents', { method: 'POST', body: fd });
      return uploaded.documentUrl || uploaded.filePath || uploaded;
    }
    if (file?.url) return file.url;
    return null;
  };
  React.useEffect(() => {
    if (id) {
      getMeasuringInstrumentCalibration(id).then(d => {
        const data: any = { ...d };
        if (data.calibrationDate) data.calibrationDate = dayjs(data.calibrationDate);
        if (data.nextCalibrationDate) data.nextCalibrationDate = dayjs(data.nextCalibrationDate);
        if (data.calibrationFilePath) {
          data.calibrationFilePath = buildFileListFromUrl(data.calibrationFilePath, '-certificate');
        }
        if (data.usageSuitabilityReportFilePath) {
          data.usageSuitabilityReportFilePath = buildFileListFromUrl(data.usageSuitabilityReportFilePath, '-report');
        }
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isResultCompliant: true });
    }
    
  }, [id, form]);
  const onFinish = async (values: CreateMeasuringInstrumentCalibrationDto) => {
    const data: any = { ...values };
    if (data.calibrationDate) data.calibrationDate = data.calibrationDate.format('YYYY-MM-DD');
    if (data.nextCalibrationDate) data.nextCalibrationDate = data.nextCalibrationDate.format('YYYY-MM-DD');
    if (Array.isArray(data.calibrationFilePath)) {
      data.calibrationFilePath = await uploadSingleDocument(data.calibrationFilePath, 'calibrationFilePath');
    } else if (data.calibrationFilePath === undefined) {
      delete data.calibrationFilePath;
    }
    if (Array.isArray(data.usageSuitabilityReportFilePath)) {
      data.usageSuitabilityReportFilePath = await uploadSingleDocument(data.usageSuitabilityReportFilePath, 'usageSuitabilityReportFilePath');
    } else if (data.usageSuitabilityReportFilePath === undefined) {
      delete data.usageSuitabilityReportFilePath;
    }
    if (referenceId !== undefined) data.measuringInstrumentInventory = referenceId;
    try {
      if (id) {
        await updateMeasuringInstrumentCalibration(id, data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı Kalibrasyonu Güncellendi');
      } else {
        await createMeasuringInstrumentCalibration(data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı Kalibrasyonu Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı Kalibrasyonu işlemi başarısız');
    }
  };
  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical'>
        <Form.Item
          name='trackingNumber'
          label='Kalibrasyon Numarası'
          rules={[{ required: true, message: 'Kalibrasyon numarası zorunludur' }]}
        >
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='calibrationDate' label='Kalibrasyon Tarihi' rules={[{ required: true, message: 'Kalibrasyon tarihi zorunludur' }]}>
          <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='nextCalibrationDate' label='Kalibrasyon Geçerlilik Tarihi' rules={[{ required: true, message: 'Kalibrasyon geçerlilik tarihi zorunludur' }]}>
          <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name='calibrationOrganization' label='Kalibrasyon Kuruluşu' rules={[{ required: true, message: 'Kalibrasyon kuruluşu zorunludur' }, { max: 255, message: 'En fazla 255 karakter girebilirsiniz' }]}>
          <Input style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='calibrationFilePath'
          label='Kalibrasyon Sertifikası'
          extra='PDF/JPG/PNG'
          valuePropName='fileList'
          getValueFromEvent={normalizeUploadEvent}
          rules={[{ required: true, message: 'Kalibrasyon sertifikası yüklenmelidir' }]}
        >
          <Upload
            beforeUpload={() => false}
            style={{ width: '100%' }}
            listType='text'
            accept='.pdf,.jpg,.jpeg,.png'
            maxCount={1}
            multiple={false}
          >
            <Button block>Dosya Seç</Button>
          </Upload>
        </Form.Item>
        <Form.Item
          name='usageSuitabilityReportFilePath'
          label='Kullanım Uygunluğunun Değerlendirme Raporu'
          extra='PDF/JPG/PNG'
          valuePropName='fileList'
          getValueFromEvent={normalizeUploadEvent}
          rules={[{ required: true, message: 'Kullanım uygunluğunun değerlendirme raporu yüklenmelidir' }]}
        >
          <Upload
            beforeUpload={() => false}
            style={{ width: '100%' }}
            listType='text'
            accept='.pdf,.jpg,.jpeg,.png'
            maxCount={1}
            multiple={false}
          >
            <Button block>Dosya Seç</Button>
          </Upload>
        </Form.Item>
        <Form.Item
          name='isResultCompliant'
          label='Sonuç'
          valuePropName='checked'
          rules={[{
            validator: (_: any, value: boolean | undefined) => (typeof value === 'boolean' ? Promise.resolve() : Promise.reject(new Error('Sonuç seçilmelidir')))
          }]}
        >
          <Switch checkedChildren='Uygun' unCheckedChildren='Uygun Değil' />
        </Form.Item>
        <Form.Item name='note' label='Not'>
          <Input.TextArea rows={3} style={{ width: '100%' }} />
        </Form.Item>
        <Button htmlType='submit' type='primary' block>Kaydet</Button>
      </Form>
    </>
  );
}
